import { useState } from 'react'
import { motion } from 'framer-motion'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'

const EASE = [0.22, 1, 0.36, 1] as const

type Swatch = {
  name: string
  hex: string
  rgb: string
  role: string
  textTone: 'light' | 'dark'
  borderRequired?: boolean
}

const swatches: Swatch[] = [
  {
    name: 'Madison Blue',
    hex: '#042860',
    rgb: 'rgb(4, 40, 96)',
    role: 'Primary brand color. Default for headers, primary buttons, and brand surfaces.',
    textTone: 'light',
  },
  {
    name: 'White',
    hex: '#FFFFFF',
    rgb: 'rgb(255, 255, 255)',
    role: 'Default canvas. Use as background for printed materials and light-mode surfaces.',
    textTone: 'dark',
    borderRequired: true,
  },
  {
    name: 'Eerie Black',
    hex: '#191919',
    rgb: 'rgb(25, 25, 25)',
    role: 'Default dark surface. Use as canvas for dark-mode and high-contrast applications.',
    textTone: 'light',
  },
]

export default function ColorPalette() {
  return (
    <Section tone="default" density="default" id="colors">
      <Container size="xl">
        <div className="max-w-3xl">
          <Eyebrow tone="accent">Colors</Eyebrow>
          <Heading as="h2" size="display-lg" className="mt-5">
            Three backgrounds.
            <br />
            Nothing else.
          </Heading>
          <Text size="xl" tone="secondary" className="mt-6">
            The Monofor logo lives on one of three approved surfaces. Choose by
            context; never paint a custom background to fit a layout.
          </Text>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {swatches.map((s, i) => (
            <motion.div
              key={s.hex}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: EASE, delay: i * 0.06 }}
            >
              <SwatchCard swatch={s} />
            </motion.div>
          ))}
        </div>
      </Container>
    </Section>
  )
}

function SwatchCard({ swatch }: { swatch: Swatch }) {
  const [copied, setCopied] = useState<string | null>(null)
  const copy = (value: string, label: string) => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(label)
      window.setTimeout(() => setCopied(null), 1400)
    })
  }

  const textClass = swatch.textTone === 'light' ? 'text-white' : 'text-brand-900'

  return (
    <div className="overflow-hidden rounded-3xl border border-line-subtle bg-surface shadow-card">
      <div
        className={`relative aspect-[5/3] ${textClass}`}
        style={{ backgroundColor: swatch.hex }}
      >
        {swatch.borderRequired && (
          <div className="absolute inset-2 rounded-2xl border border-line" />
        )}
        <div className="absolute bottom-5 left-6 right-6 flex items-end justify-between">
          <div className="text-display-sm font-medium">{swatch.name}</div>
          <div className="text-body-sm opacity-70 tabular-nums">{swatch.hex}</div>
        </div>
      </div>
      <div className="p-6">
        <Text size="sm" tone="tertiary">
          {swatch.role}
        </Text>
        <div className="mt-5 flex flex-wrap gap-2">
          <CopyChip
            label={swatch.hex}
            copied={copied === 'hex'}
            onClick={() => copy(swatch.hex, 'hex')}
          />
          <CopyChip
            label={swatch.rgb}
            copied={copied === 'rgb'}
            onClick={() => copy(swatch.rgb, 'rgb')}
          />
        </div>
      </div>
    </div>
  )
}

function CopyChip({
  label,
  copied,
  onClick,
}: {
  label: string
  copied: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-pill border border-line-subtle bg-surface-subtle px-3 py-1.5 text-caption font-medium tabular-nums text-ink-secondary transition hover:border-line hover:text-ink-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
    >
      {copied ? (
        <>
          <CheckGlyph className="h-3 w-3 text-accent-600" />
          Copied
        </>
      ) : (
        <>
          <CopyGlyph className="h-3 w-3 text-ink-tertiary" />
          {label}
        </>
      )}
    </button>
  )
}

function CopyGlyph({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none">
      <rect x="5" y="5" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M11 5V4a1.5 1.5 0 0 0-1.5-1.5h-6A1.5 1.5 0 0 0 2 4v6A1.5 1.5 0 0 0 3.5 11.5H5" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  )
}

function CheckGlyph({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none">
      <path d="M3 8.4 6.2 11.6 13 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
